import React, { useRef, useState, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import Icon from 'react-native-vector-icons/dist/MaterialIcons';
import Feather from 'react-native-vector-icons/dist/Feather';
import {
    View,
    TextInput,
    Image,
    TouchableOpacity,
    Text,
    ActivityIndicator,
    ProgressBar,
    Keyboard
} from 'react-native-web';
import MaskInput from 'react-native-mask-input';

// Utils
import { checkPasswordStrength } from '../utils/util_helpers';

// Configs
import GLOBAL from '../configs/config_global';

export function TextBox(props, ref) {
    // region Props
    const {
        title,
        onTitlePressed,
        useMaskedInput,
        customImage,
        customImageNoTint,
        customImageStyle,
        useFeather,
        imageName,
        imageColor,
        imageSize,
        showError,
        errorMessage,
        placeholder,
        placeholderTextColor,
        secureTextEntry,
        autoCapitalize,
        returnKeyType,
        onPress,
        textInputStyle,

        showHintButton,
        hintButtonImage,
        hintButtonOpacity,
        hintButtonOnPress,
        hintButtonColor,

        onSubmitEditing,
        multiline,
        blurOnSubmit,
        isLoading,
        onLongPress,
        isSingleInput,
        showPasswordValidator,
        contentBoxStyle,
        disabled,
        prefix,
        onFocus,
        onBlur,
        showEmptyIndicator,
        renderExtraContent,
        maskArray,
        onChangeText,
        useBlankStyle,
        value,
        editable,
        maxHeight,
        onContentSizeChange,
        keyboardType,
        maxLength,
        onEndEditing,
        selection,
        style,
        customOnPressIcon
    } = props;
    // endregion

    // region Refs
    const outputValue = useRef('');
    const inputText = useRef(null);
    // endregion

    // region Local State
    const [focused, setFocused] = useState(false);
    const [textBoxWidth, setTextBoxWidth] = useState(0);
    // endregion

    // region Function - Get Output Value
    const getOutputValue = () => {
        if (!useMaskedInput) return('');
        return(outputValue.current);
    }
    // endregion

    // region Function - Focus to Text Input
    const focus = () => {
        inputText.current?.focus();
    }
    // endregion

    // region Function - Focus to Text Input
    const blur = () => {
        inputText.current?.blur();
    }
    // endregion

    // region Expose Methods
    useImperativeHandle(ref, () => ({ focus, blur }), [inputText]);
    // endregion

    // region Render Hint Button
    const renderHintButton = () => (
        showHintButton ?
            (
                isLoading ?
                    <ActivityIndicator color={hintButtonColor}/>
                    :
                    <View style={{ marginLeft: 6 }}>
                        <TouchableOpacity onPress={hintButtonOnPress}>
                            {/* Image */}
                            <Icon
                                name={hintButtonImage}
                                size={20}
                                color={hintButtonColor || GLOBAL.COLOR.TEXT_GRAY}
                                style={{ opacity: hintButtonOpacity }}
                            />
                        </TouchableOpacity>
                    </View>
            )
            :
            null
    )
    // endregion

    // region Render Image/Title
    const renderImageOrTitle = () => {
        const MyView = errorMessage?.trim() ? TouchableOpacity : View;
        const MyIcon = useFeather ? Feather : Icon;
        return(
            prefix?.trim() ?
                <Text style={{ color: !!value?.trim() ? (textInputStyle?.color || GLOBAL.COLOR.BASIC.BLACK_TWO) : GLOBAL.COLOR.TEXT_LIGHT_GRAY, marginRight: 4, marginTop: !!value?.trim() ? 16 : 0 }}>
                    {prefix?.trim() || ''}
                </Text>
                :
                <MyView style={{ marginTop: multiline ? 20 : 0, marginRight: 4 }} onPress={() => alert(errorMessage)}>
                    {
                        imageName ?
                            <MyIcon
                                name={imageName}
                                size={imageSize}
                                color={showError ? GLOBAL.COLOR.GOOGLE : imageColor}
                            />
                            :
                            (
                                customImage ?
                                    <Image
                                        source={customImage}
                                        tintColor={customImageNoTint ? undefined : imageColor}
                                        style={customImageStyle}
                                        resizeMode={'contain'}
                                    />
                                    :
                                    null
                            )
                    }
                </MyView>
        );
    }
    // endregion

    // region Render Main Text Input
    const getPropTextInputStyle = () => {
        let propTextInputStyle = {};
        if (Array.isArray(textInputStyle)) {
            textInputStyle.forEach((obj) => Object.assign(propTextInputStyle, obj));
        }
        else propTextInputStyle = textInputStyle;

        return propTextInputStyle;
    }

    const renderMainTextInput = () => {
        const textInputStyle = Object.assign(
            {
                outlineStyle: 'none',
                color: showError ? GLOBAL.COLOR.BASIC.RED : GLOBAL.COLOR.BASIC.BLACK_TWO,
                flex: 1, marginLeft: (imageName || customImage) ? 12 : 0, paddingTop: value?.trim() ? 8 : 0,
                backgroundColor: (showEmptyIndicator && !value?.trim()) ? 'rgba(237, 59, 59, 0.3)' : 'transparent',
                fontSize: props.fontSize || 18, verticalAlign: multiline ? 'top' : 'center'
            },
            getPropTextInputStyle() || {}
        );

        const TextInputComp = useMaskedInput ? MaskInput : TextInput;
        return(
            <TextInputComp
                ref={inputText}
                style={textInputStyle}
                readOnly={!editable || disabled}
                secureTextEntry={secureTextEntry}
                autoCorrect={false}
                autoCapitalize={autoCapitalize}
                enterKeyHint={returnKeyType}
                placeholder={placeholder || (useMaskedInput ? 'Phone Number' : '')}
                placeholderTextColor={placeholderTextColor || GLOBAL.COLOR.NEUTRAL_LIGHT_GREY}
                underlineColorAndroid={'transparent'}
                maxHeight={maxHeight}
                onContentSizeChange={onContentSizeChange}
                onChangeText={
                    useMaskedInput ?
                        (masked, unmasked) => onChangeText(unmasked)
                        :
                        (text) => onChangeText(text)
                }
                value={value}
                blurOnSubmit={blurOnSubmit || false}
                onFocus={() => {
                    setFocused(true);
                    onFocus();
                }}
                onBlur={() => {
                    setFocused(false);
                    onBlur();
                }}
                multiline={multiline}
                autoGrow={multiline}
                inputMode={keyboardType || 'default'}
                mask={maskArray || ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, ' - ', /\d/, /\d/, /\d/, /\d/]}
                maxLength={maxLength}
                onSubmitEditing={(e) => {
                    e.stopPropagation();
                    Keyboard.dismiss();
                    onSubmitEditing(e.nativeEvent.text);
                }}
                onEndEditing={onEndEditing}
                selection={selection}
            />
        );
    }
    // endregion

    // region Render Password Strength
    const renderPasswordStrength = () => {
        const passwordStrength = checkPasswordStrength(value) || 0;
        return(
            (showPasswordValidator && value?.trim()) ?
                <View
                    onLayout={(event) => {
                        const newWidth = event.nativeEvent.layout.width;
                        if (!textBoxWidth) setTextBoxWidth(newWidth);
                    }}
                    style={{ flexDirection: 'row', alignItems: 'center', width: '100%', marginTop: 8 }}>
                    <ProgressBar
                        progress={passwordStrength}
                        color={passwordStrength === 1 ? GLOBAL.COLOR.BASIC.GREEN : (passwordStrength === 0.5 ? GLOBAL.COLOR.BASIC.BLUE : (passwordStrength === 0.2 ? GLOBAL.COLOR.BASIC.ORANGE : GLOBAL.COLOR.TEXT_LIGHT_GRAY))}
                        trackColor={GLOBAL.COLOR.INNER_GRAY}
                        style={{ flex: 1, height: 8, borderRadius: 8 }}
                    />

                    <Text style={{ fontSize: 16, marginLeft: 12, textAlign: 'right', color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, fontWeight: '500' }}>
                        {passwordStrength !== 0 ? (passwordStrength === 0.5 ? 'Medium' : (passwordStrength === 1 ? 'Strong' : 'Weak')) : 'Invalid'}
                    </Text>
                </View>
                :
                false
        );
    }
    // endregion

    // region MAIN RENDER
    const MyView = onPress ? TouchableOpacity : View;
    return(
        <View style={[{ alignSelf: 'stretch', opacity: !disabled ? 1 : 0.5, marginTop: 12 }].concat(style)}>
            <MyView
                style={
                    [{
                        width: '100%', alignItems: multiline ? 'flex-start' : 'center', flexDirection: 'row', paddingHorizontal: 12,
                        borderWidth: useBlankStyle ? 0 : 2, borderRadius: 6,
                        borderColor: showError ? GLOBAL.COLOR.BASIC.RED : (focused ? GLOBAL.COLOR.BASIC.GRAY : GLOBAL.COLOR.DIVIDER)
                    }].concat(contentBoxStyle)
                }
                onPress={onPress}
                onLongPress={onLongPress}>
                {/* Text Box Image */}
                {renderImageOrTitle()}

                {/* Text Input */}
                <View style={{ flex: 1, height: '100%' }}>
                    {/* Title */}
                    {
                        !!title?.trim() &&
                        <Text style={{ fontSize: 12, fontWeight: 'bold', color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, marginTop: 8, marginLeft: (imageName || customImage) ? 12 : 0, opacity: !!value?.trim() ? 1 : 0 }}>
                            {title}
                        </Text>
                    }

                    {/* Main Input */}
                    {
                        (!!onPress || !!onLongPress) ?
                            <Text
                                style={Object.assign(
                                    { color: !value ? GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY : GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: '450', flex: 1, marginLeft: (imageName || customImage) ? 10 : 0, marginRight: 5, fontSize: 16 },
                                    getPropTextInputStyle(),
                                    { paddingTop: !!value?.trim() ? 12 : 0 }
                                )}
                                numberOfLines={1}>
                                {value || placeholder}
                            </Text>
                            :
                            renderMainTextInput()
                    }

                    {/* Fake Title */}
                    {
                        !!title?.trim() &&
                        <Text style={{ fontSize: 12, fontWeight: 'bold', marginBottom: value?.trim() ? 0 : 8, opacity: 0 }}>
                            {title}
                        </Text>
                    }
                </View>

                {/* On Press Arrow */}
                {
                    (!!onPress || !!onLongPress) ?
                        (
                            showHintButton ?
                                renderHintButton()
                                :
                                (
                                    customOnPressIcon?.startsWith('data:image/') ?
                                        <Image source={customOnPressIcon} style={{ width: 28, height: 28 }} />
                                        :
                                        <Icon name={customOnPressIcon} size={28} color={'black'} />
                                )
                        )
                        :
                        renderHintButton()
                }
            </MyView>

            {/* Password Strength */}
            {renderPasswordStrength()}
        </View>
    );
    // endregion
}

// Forward Refs
TextBox = React.forwardRef(TextBox);

// region Props Define
TextBox.propTypes = {
    title: PropTypes.string,
    onTitlePressed: PropTypes.func,
    useMaskedInput: PropTypes.bool,
    onValueCompleted: PropTypes.func,
    customImage: PropTypes.any,
    customImageNoTint: PropTypes.bool,
    customImageStyle: PropTypes.object,
    useFeather: PropTypes.bool,
    imageName: PropTypes.string,
    imageColor: PropTypes.string,
    imageSize: PropTypes.number,
    showError: PropTypes.bool,
    errorMessage: PropTypes.string,
    placeholder: PropTypes.string,
    placeholderTextColor: PropTypes.string,
    secureTextEntry: PropTypes.bool,
    autoCorrect: PropTypes.bool,
    autoCapitalize: PropTypes.string,
    returnKeyType: PropTypes.string,
    onPress: PropTypes.func,
    textInputStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    textBoxColor: PropTypes.string,

    showHintButton: PropTypes.bool,
    hintButtonImage: PropTypes.any,
    hintButtonOpacity: PropTypes.number,
    hintButtonOnPress: PropTypes.func,
    hintButtonColor: PropTypes.string,
    blinkHintButton: PropTypes.bool,

    onSubmitEditing: PropTypes.func,
    multiline: PropTypes.bool,
    blurOnSubmit: PropTypes.bool,
    isLoading: PropTypes.bool,
    onLongPress: PropTypes.func,
    isSingleInput: PropTypes.bool,
    containerStyle: PropTypes.object,
    showPasswordValidator: PropTypes.bool,
    contentBoxStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    appTourKey: PropTypes.string,
    disabled: PropTypes.bool,
    prefix: PropTypes.string,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    showEmptyIndicator: PropTypes.bool,

    renderExtraContent: PropTypes.func,
    maskArray: PropTypes.array,
    onChangeText: PropTypes.func,
    useBlankStyle: PropTypes.bool,
    value: PropTypes.string,
    editable: PropTypes.bool,
    maxHeight: PropTypes.number,
    onContentSizeChange: PropTypes.func,
    keyboardType: PropTypes.string,
    maxLength: PropTypes.number,
    onEndEditing: PropTypes.func,
    selection: PropTypes.any,
    style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),

    customOnPressIcon: PropTypes.any
};

TextBox.defaultProps = {
    title: '',
    onSubmitEditing: () => {},
    onTitlePressed: () => {},
    useMaskedInput: false,
    onValueCompleted: () => {},
    imageColor: GLOBAL.COLOR.TEXT_GRAY,
    showError: false,
    showHintButton: false,
    isLoading: false,
    customImage: null,
    customImageNoTint: false,
    useFeather: false,
    imageSize: 32,
    placeholder: '',
    placeholderTextColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY,
    customImageStyle: { width: 30, height: 20, marginTop: 2 },
    isSingleInput: false,
    containerStyle: {},
    showPasswordValidator: false,
    contentBoxStyle: {},
    appTourKey: '',
    disabled: false,
    prefix: '',
    onFocus: () => {},
    onBlur: () => {},
    showEmptyIndicator: false,
    blinkHintButton: false,
    textInputStyle: {},
    renderExtraContent: () => <View/>,
    maskArray: null,
    onChangeText: () => {},
    useBlankStyle: false,
    value: '',
    editable: true,
    style: {},
    customOnPressIcon: 'keyboard-arrow-right'
};
// endregion
